// Browser notifications (D215, the user's ask): "when it is your turn, or there is a
// trade offer you have the resources for, you should get notified if the browser window
// is not active."
//
// This is the IN-TAB half — the Notification API fires while the game's tab is open but
// hidden (another window, another tab, phone screen off with the tab alive). The
// closed-tab/mobile-push half is Web Push and needs server infrastructure (a service
// worker, a subscription store, a VAPID sender on turn events); it is designed as its own
// slice, not smuggled in here. No native app is required for either half.
//
// Three gates, every one evaluated PER FIRE (the D212 posture — "off means it never
// happens", not "it waits"): the player's Settings toggle, the browser permission, and
// the tab actually being hidden. A visible tab never notifies — the board itself is the
// notification. Everything is wrapped: a notification is never worth an error, and jsdom
// has no Notification at all.

export interface Notifier {
  /** Does this browser have the API at all? (jsdom and some webviews do not.) */
  supported(): boolean;
  permission(): NotificationPermission;
  /** Ask the browser — must be called from a user gesture (the Settings button). */
  request(): Promise<NotificationPermission>;
  /** The viewer's window just opened. */
  turn(): void;
  /** A standing offer the viewer can actually afford to accept. */
  offer(fromName: string, deal: string): void;
}

interface NotificationLike {
  close(): void;
  onclick: ((this: unknown, ev: unknown) => void) | null;
}

export interface NotifierOpts {
  doc: Document;
  /** The Settings toggle — read per fire, never cached. */
  enabled(): boolean;
  /**
   * D250/A6: a notification was CLICKED — the player came back because of it.
   *
   * Reported here rather than counted at the send site on purpose: a notification fired is
   * a thing we did, a notification clicked is a thing that worked, and only the second one
   * says whether the feature earns its permission prompt.
   */
  onClick?(): void;
  /** Injected in tests; defaults to the real constructor. */
  api?: {
    permission: NotificationPermission;
    requestPermission(): Promise<NotificationPermission>;
    make(title: string, init: { body?: string; tag?: string }): NotificationLike;
  };
}

/** The real Notification constructor behind the injectable seam, when the browser has one. */
function realApi(): NotifierOpts['api'] | null {
  const N = (globalThis as { Notification?: typeof Notification }).Notification;
  if (typeof N !== 'function') return null;
  return {
    get permission() {
      return N.permission;
    },
    requestPermission: () => N.requestPermission(),
    make: (title, init) => new N(title, init) as unknown as NotificationLike,
  };
}

export function createNotifier(opts: NotifierOpts): Notifier {
  const api = opts.api ?? realApi();

  const fire = (title: string, body: string, tag: string): void => {
    try {
      if (!api || api.permission !== 'granted') return;
      if (!opts.enabled()) return;
      if (opts.doc.visibilityState !== 'hidden') return; // a visible board IS the notification
      const n = api.make(title, { body, tag }); // tag: a re-fire replaces, never stacks
      n.onclick = () => {
        try {
          opts.onClick?.();
          opts.doc.defaultView?.focus();
          n.close();
        } catch {
          /* focus refusal is the browser's right */
        }
      };
    } catch {
      // a notification is never worth an error
    }
  };

  return {
    supported: () => api !== null,
    permission: () => api?.permission ?? 'denied',
    request: async () => {
      if (!api) return 'denied';
      try {
        return await api.requestPermission();
      } catch {
        return 'denied';
      }
    },
    turn: () => fire('KOLONY — your turn', 'The table is waiting on you.', 'kolony-turn'),
    offer: (fromName, deal) => fire(`KOLONY — ${fromName} offers a trade`, `${deal} — you can afford it.`, 'kolony-offer'),
  };
}
